import { useState, useEffect, useContext } from "react";
import { openDB } from "idb";
import { constrainLength, constrainLineLengths } from "../utils/form";
import styles from "../css/SetupForm.module.css";
import { getData, updateServiceStatus } from "../utils/api";
import Context from "../context/context";
const DEFAULT_DELIVERY_SETUP = {
  ordering_allowance: 0,
  ordering_minimum_bill_amount: 1000,
  delivery_charge_condition: 0,
  delivery_charge_bill_amount: 100,
  dellivery_charges: 1000,
  default_mode: "0",
  payment_modes: [],
  customer_location: 0,
  message_service_available: "Service is available!",
  message_service_unavailable: "Service is unavailable!",
  "message_after-order": "Order placed!",
  notification_sms_confirmation: true,
  notification_sms_order_ready: true,
};

const DEFAULT_PAYMENT_MODES = [
  {
    _id: "61b30b858ded1602f2a23320",
    maintain_customer_balance: "Y",
    outlet_wise_details: [
      {
        _id: "61e3daa107ff0d681d8e06e9",
        outlet_uuid: "outlet-uuid-1",
        status: "Loading...",
      },
      { _id: "61e3daa107ff0d681d8e06ea", outlet_uuid: "", status: "" },
    ],
    payment_mode_name: "Loading...",
    payment_mode_uuid: "1",
  },
];

const DELIVERY_SETUP_MAX_DIGITS = {
  ordering_minimum_bill_amount: 4,
  delivery_charge_bill_amount: 3,
  dellivery_charges: 4,
};

const DELIVERY_SETUP_MESSAGE_LINES = {
  message_service_available: [5, 40],
  message_service_unavailable: [5, 40],
  "message_after-order": [5, 40],
};

export default function DeliverySetupForm({ onSave }) {
  const [deliverySetup, setDeliverySetup] = useState(DEFAULT_DELIVERY_SETUP);
  const [dbPaymentModes, setDbPaymentModes] = useState(DEFAULT_PAYMENT_MODES);
  const [serviceStatus, setServiceStatus] = useState(2);
  const [online, setOnline] = useState(navigator.onLine);
  const context = useContext(Context);
  const { updateUnSync } = context;

  useEffect(() => (async function asyncUseEffect() {
    const db = await openDB(
      "FoodDo",
      +localStorage.getItem("IDBVersion") || 1
    );
    const paymentModeStore = db.transaction("payment_modes", "readonly").objectStore("payment_modes");
    const paymentModes = await paymentModeStore.getAll();
    console.debug(paymentModes);
    setDbPaymentModes([
      ...paymentModes,
      { payment_mode_name: 'Cash on Delivery', payment_mode_uuid: "0", hardCoded: 1 },
      { payment_mode_name: 'UPI', payment_mode_uuid: "UPI", hardCoded: 1 },
    ]);

    const outletStore = db
      .transaction("outlet", "readonly")
      .objectStore("outlet");
    const currentOutlet = (await outletStore.getAll())[0];
    const dbDeliverySetup = currentOutlet.delivery_setup;
    const filteredDbDeliverySetup = Object.fromEntries(
      Object.entries(dbDeliverySetup).filter(([key]) =>
        Object.keys(DEFAULT_DELIVERY_SETUP).includes(key)
      )
    );
    setDeliverySetup(
      unwrapDbPickupState({
        ...DEFAULT_DELIVERY_SETUP,
        ...filteredDbDeliverySetup,
      }),
    );
    setOnline(navigator.onLine);

    const data = await getData();
    const [{ service_status }] = data.filter(
      (service) => service.service_name === "Delivery" && service
    );
    setServiceStatus(service_status.toLowerCase() === "y" ? 0 : 1);
  })(),
    []
  );

  const getFormattedDeliverySetup = () => ({
    ...deliverySetup,
    notification_sms_confirmation: String(
      Number(deliverySetup.notification_sms_confirmation)
    ),
    notification_sms_order_ready: String(
      Number(deliverySetup.notification_sms_order_ready)
    ),
  });

  const unwrapDbPickupState = (dbPickupState) => ({
    ...dbPickupState,
    notification_sms_confirmation: Boolean(
      Number(dbPickupState.notification_sms_confirmation)
    ),
    notification_sms_order_ready: Boolean(
      Number(dbPickupState.notification_sms_order_ready)
    ),
  });

  const handleMultipleSelections = (value) => {
    if (!deliverySetup.payment_modes.includes(value)) {
      setDeliverySetup({
        ...deliverySetup,
        payment_modes: [...(
          dbPaymentModes
            ?.filter(i => deliverySetup?.payment_modes?.includes(i.payment_mode_uuid))
            ?.map(i => i.payment_mode_uuid) || []), value],
      });
    } else {
      const payment_modes = deliverySetup.payment_modes.filter(i => i !== value)
      const default_mode = deliverySetup?.default_mode === value || payment_modes?.length === 1
        ? payment_modes?.[0]
        : deliverySetup?.default_mode

      setDeliverySetup({
        ...deliverySetup,
        default_mode,
        payment_modes,
      });
    }
  };

  const handleChange = (event) => {
    const getValue = (e) => (e.type === "checkbox" ? e.checked : e.value);
    const getSelectedValues = (e) =>
      [...e.options].filter((e) => e.selected).map((e) => e.value);

    const value = event.target.multiple
      ? getSelectedValues(event.target)
      : getValue(event.target);
    const name = event.target.name;
    const isMessage = Object.keys(DELIVERY_SETUP_MESSAGE_LINES).includes(name);
    const isNumber = Object.keys(DELIVERY_SETUP_MAX_DIGITS).includes(name);
    const isPayment = name === "payment_modes";
    const constrainedValue = isMessage
      ? constrainLineLengths(value, ...DELIVERY_SETUP_MESSAGE_LINES[name])
      : isNumber
        ? constrainLength(String(value), DELIVERY_SETUP_MAX_DIGITS[name])
        : isPayment
          ? [...new Set(value), "0"] // always include "Cash on Delivery"
          : value;

    setDeliverySetup({
      ...deliverySetup,
      [event.target.name]: constrainedValue,
    });
  };

  // On setup submit
  const handleSubmit = async (event) => {
    event.preventDefault();
    const db = await openDB("FoodDo", +localStorage.getItem("IDBVersion") || 1);
    const outletStore = db.transaction("outlet", "readwrite").objectStore("outlet");
    const value = (await outletStore.getAll())[0];
    const updatedValue = {
      ...value,
      delivery_setup: getFormattedDeliverySetup(),
    };
    // console.log(updatedValue);
    await outletStore.put(updatedValue);
    handleDeliveryUnsyncedFile(updatedValue);
    onSave();
  };

  const handleStatusChange = async (e) => {
    const value = parseInt(e.target.value);
    const status = value === 0 ? "Y" : "N";
    setServiceStatus(value);
    await updateServiceStatus("Delivery", { service_status: status });
  };

  // Adding/Editing  outlet details to unsynced_files collection in IndexedDB
  const handleDeliveryUnsyncedFile = async (data) => {
    const db = await openDB("FoodDo", +localStorage.getItem("IDBVersion") || 1);
    const tx = await db.transaction("unsynced_files", "readwrite").objectStore("unsynced_files");
    const ndata = {
      IDENTIFIER: "delivery_setup",
      RECON_TIME: Date.now(),
      delivery_setup: data.delivery_setup,
    };
    await tx.put(ndata);
    handleDeliveryReconciliation(ndata);
    updateUnSync()
  };

  // Adding/Editing delivery to reconciliation collection in IndexedDB
  const handleDeliveryReconciliation = async (data) => {
    const db = await openDB("FoodDo", +localStorage.getItem("IDBVersion") || 1);
    const tx = await db
      .transaction("reconciliation", "readwrite")
      .objectStore("reconciliation");
    const allReconRecords = await tx.getAll();

    const othersJsonExists = allReconRecords.find(
      (curr) => curr.IDENTIFIER === "others"
    );
    if (othersJsonExists) {
      const delivery = othersJsonExists?.details?.filter(
        (entry) => entry.type !== "delivery_setup"
      );
      const editData = {
        ...othersJsonExists,
        details: [
          ...delivery,
          {
            type: "delivery_setup",
            RECON_TIME: data.RECON_TIME,
          },
        ],
      };
      await tx.put(editData);
    } else {
      const newdata = {
        IDENTIFIER: "others",
        details: [
          {
            type: "delivery_setup",
            RECON_TIME: data.RECON_TIME,
          },
        ],
      };
      await tx.add(newdata);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.heading}>
        <h1>Delivery Setup</h1>
      </div>
      <div className={styles.formContainer}>
        <div className={styles.status}>
          <h5>Status</h5>
          <select
            name="service_status"
            className={styles.select}
            disabled={!online}
            value={serviceStatus}
            onChange={handleStatusChange}>
            {online ? (
              serviceStatus === 2 ? (
                <option value={2}>Loading...</option>
              ) : (
                <>
                  <option value={0}>Enabled</option>
                  <option value={1}>Disabled</option>
                </>
              )
            ) : (
              <option selected>Check your connection</option>
            )}
          </select>
        </div>

        <div className={styles.formGroup}>
          <h2>General</h2>

          <div className={styles.row}>
            <label className={styles.selectLabel}>
              Ordering allowance
              <select
                name="ordering_allowance"
                className={styles.select}
                value={deliverySetup.ordering_allowance}
                onChange={handleChange}>
                <option value={0}>Always allowed</option>
                <option value={1}>Above minimum bill amount</option>
              </select>
            </label>

            <label className={styles.selectLabel}>
              Minimum bill amount
              <input
                type="number"
                name="ordering_minimum_bill_amount"
                className={styles.numberInput}
                value={deliverySetup.ordering_minimum_bill_amount}
                onChange={handleChange}
                disabled={+deliverySetup.ordering_allowance !== 1}
              />
            </label>
          </div>

          <div className={styles.row}>
            <label className={styles.selectLabel}>
              Delivery charge condition
              <select
                name="delivery_charge_condition"
                className={styles.select}
                value={deliverySetup.delivery_charge_condition}
                onChange={handleChange}>
                <option value={0}>Never apply</option>
                <option value={1}>Always apply</option>
                <option value={2}>Apply below bill amount</option>
              </select>
            </label>

            <label className={styles.selectLabel}>
              Bill Amount
              <input
                type="number"
                name="delivery_charge_bill_amount"
                className={styles.numberInput}
                value={deliverySetup.delivery_charge_bill_amount}
                onChange={handleChange}
                disabled={+deliverySetup.delivery_charge_condition !== 2}
              />
            </label>

            <label className={styles.selectLabel}>
              Delivery Charges
              <input
                type="number"
                name="dellivery_charges"
                className={styles.numberInput}
                value={deliverySetup.dellivery_charges}
                onChange={handleChange}
                disabled={+deliverySetup.delivery_charge_condition === 0}
              />
            </label>
          </div>

          <div className={styles.row}>
            <div className={styles.selectLabel}>
              <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Payment Modes</span>
                <span style={{ color: "#7f7f8b", fontSize: "small", marginTop: "8px", }}>Default</span>
              </p>
              <div className={`${styles.selections} ${styles.layout}`}>
                {Object.values(dbPaymentModes)
                  .filter(paymentMode => paymentMode.maintain_customer_balance === "Y" || paymentMode.hardCoded)
                  .map(({ payment_mode_uuid, payment_mode_name, ...paymentMode }) => {
                    const checked = deliverySetup.payment_modes.includes(payment_mode_uuid)
                    return (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '5px' }}>
                        <input
                          type="checkbox"
                          name="payment-mode"
                          id={payment_mode_uuid}
                          checked={checked}
                          onChange={e => handleMultipleSelections(payment_mode_uuid)}
                          style={{ marginRight: '10px' }}
                          disabled={checked && deliverySetup.payment_modes.length === 1}
                        />
                        <label htmlFor={payment_mode_uuid} style={{ width: '100%' }}>
                          {payment_mode_name}
                        </label>
                        {checked && <input
                          type="radio"
                          name="default-payment-mode"
                          checked={deliverySetup.default_mode === payment_mode_uuid}
                          onChange={e => e.currentTarget.checked && checked && setDeliverySetup(doc => ({
                            ...doc, default_mode: payment_mode_uuid
                          }))}
                        />}
                      </div>
                    )
                  })}

              </div>
            </div>

            <label className={styles.selectLabel}>
              Customer Location
              <select
                name="customer_location"
                className={styles.select}
                value={deliverySetup.customer_location}
                onChange={handleChange}>
                <option value={0}>Never ask</option>
                <option value={1}>Always ask before placing an order</option>
                <option value={2}>Ask on new address only</option>
              </select>
            </label>
          </div>
        </div>

        <div className={styles.formGroup}>
          <h2>Display Message</h2>

          <div className={styles.row}>
            <label className={styles.selectLabel}>
              When service unavailable
              <textarea
                name="message_service_unavailable"
                className={styles.messageTextarea}
                value={deliverySetup.message_service_unavailable}
                onChange={handleChange}
              />
            </label>

            <label className={styles.selectLabel}>
              When service available
              <textarea
                name="message_service_available"
                className={styles.messageTextarea}
                value={deliverySetup.message_service_available}
                onChange={handleChange}
              />
            </label>

            <label className={styles.selectLabel}>
              After placing order
              <textarea
                name="message_after-order"
                className={styles.messageTextarea}
                value={deliverySetup["message_after-order"]}
                onChange={handleChange}
              />
            </label>
          </div>
        </div>

        <div className={styles.formGroup}>
          <h2>Notification Settings</h2>

          <div className={styles.row}>
            <label className={styles.checkboxLabel}>
              <input
                name="notification_sms_confirmation"
                type="checkbox"
                checked={deliverySetup.notification_sms_confirmation}
                onChange={handleChange}
              />
              Send SMS for Order Accept / Declined
            </label>

            <label className={styles.checkboxLabel}>
              <input
                name="notification_sms_order_ready"
                type="checkbox"
                checked={deliverySetup.notification_sms_order_ready}
                onChange={handleChange}
              />
              Send SMS for Order Ready
            </label>
          </div>
        </div>

        <button type="submit" className={styles.submit}>
          Save changes
        </button>

      </div>
    </form>
  );
}